import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from '@noor/lib';
import { geocoderSearch, type LegacyGeoResult } from '@/entities/legacy-geo';

export interface PickedAddress {
  address: string;
  latitude: number;
  longitude: number;
}

interface AddressSearchInputProps {
  /** Id the field's `<label htmlFor>` points at. */
  id: string;
  /** The COMMITTED address (form value) — what the box snaps back to on blur. */
  value: string;
  placeholder: string;
  className: string;
  invalid?: boolean;
  /** Fired when an option is chosen — the only way the address changes here. */
  onSelect: (picked: PickedAddress) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 6;

/**
 * Search-as-you-type address box for the branch form. Typing queries the
 * geocoder (same backend as the map picker's search) and lists the matches;
 * choosing one writes its text AND coordinates via `onSelect`.
 *
 * The typed text is never committed: an address is only valid paired with the
 * point it resolves to, so on blur (or Escape) the box reverts to the last
 * committed `value`. The list is a positioned dropdown — the nearest
 * positioned ancestor (the field's pill) sizes it.
 */
export function AddressSearchInput({
  id,
  value,
  placeholder,
  className,
  invalid,
  onSelect,
}: AddressSearchInputProps) {
  const { t } = useTranslation('eats');
  // `null` = not typing: the box shows the committed `value`, so external
  // changes (map picker confirm, form reset/seed) need no syncing effect.
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? value;
  const [results, setResults] = useState<LegacyGeoResult[] | null>(null);
  const requestId = useRef(0);

  const runSearch = useDebouncedCallback((query: string) => {
    const current = ++requestId.current;
    if (query.trim() === '') {
      setResults(null);
      return;
    }
    void (async () => {
      try {
        const found = await geocoderSearch(query, SEARCH_LIMIT);
        if (requestId.current === current) setResults(found);
      } catch {
        if (requestId.current === current) setResults([]);
      }
    })();
  }, SEARCH_DEBOUNCE_MS);

  const revert = () => {
    // Invalidate any in-flight lookup so it cannot reopen the list after blur.
    requestId.current++;
    setResults(null);
    setDraft(null);
  };

  return (
    <>
      <input
        id={id}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        aria-invalid={invalid}
        className={className}
        onChange={(event) => {
          setDraft(event.target.value);
          runSearch(event.target.value);
        }}
        onBlur={revert}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && results !== null) {
            event.stopPropagation();
            revert();
          }
        }}
      />

      {results !== null && (
        <ul className="absolute top-full right-0 left-0 z-20 mt-1 max-h-[240px] overflow-y-auto rounded-2xl border border-surface-border bg-surface-bg p-1 shadow-lg">
          {results.length === 0 ? (
            <li className="p-3 text-sm text-muted-foreground">
              {t('branches.form.addressSearchEmpty')}
            </li>
          ) : (
            results.map((result, index) => (
              <li key={`${result.address}-${index}`}>
                <button
                  type="button"
                  // Keep focus in the input so blur (and its revert) doesn't
                  // fire before the click is handled.
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={() => {
                    requestId.current++;
                    setResults(null);
                    setDraft(null);
                    onSelect({
                      address: result.address,
                      latitude: result.point.lat,
                      longitude: result.point.long,
                    });
                  }}
                  className="flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-xl p-3 text-left transition-colors hover:bg-muted"
                >
                  <span className="truncate text-sm font-medium text-foreground">
                    {result.address}
                  </span>
                  {result.subtitle !== undefined && result.subtitle !== '' && (
                    <span className="truncate text-xs text-muted-foreground">
                      {result.subtitle}
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </>
  );
}
